import React, { useCallback, useMemo } from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, FastForward, FastRewind } from '@mui/icons-material';
import type { ReportDateSettings } from 'c-reports/Types';
import { ReportDateTimeframe } from 'c-reports/Types';
import { Translate, useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import TimeFrameSwitcher from './TimeFrameSwitcher';
import DateRangeDropdown from './DateRangeDropdown';
import DateDropdown from './DateDropdown';
import {
    firstRange,
    isNextDisabled,
    isPreviousDisabled,
    lastRange,
    nextRange,
    previousRange,
} from './dateNavigation';

type Props = ReportDateSettings & {
    startDate: Date;
    endDate: Date;
    onChange: (timeframe: ReportDateTimeframe, start: Date, end: Date) => void;
    onSwitchTimeframe: (timeframe: ReportDateTimeframe) => void;
    hideDropdown?: boolean;
};

const ReportDateSwitcher: React.FC<Props> = ({
    startDate,
    endDate,
    onChange,
    minDate,
    maxDate,
    availableTimeframes,
    onSwitchTimeframe,
    timeframe,
    showGoStart,
    showGoEnd,
    hideDropdown = false,
}) => {
    const t = useCommonTranslation();
    const startDateRange = useMemo(() => firstRange(timeframe, minDate), [timeframe, minDate]);

    const endDateRange = useMemo(() => lastRange(timeframe, maxDate), [timeframe, maxDate]);

    const { formatDateString } = useDateUtils();
    const nextPrevRange = useMemo(
        () => previousRange(timeframe, startDate, endDate),
        [timeframe, endDate, startDate],
    );

    const prevDisabled = useMemo(
        () => isPreviousDisabled(timeframe, minDate, nextPrevRange),
        [minDate, nextPrevRange, timeframe],
    );

    const nextDisabled = useMemo(
        () => isNextDisabled(timeframe, maxDate, endDate),
        [maxDate, endDate, timeframe],
    );

    const onStart = useCallback(() => {
        if (!prevDisabled) {
            onChange(timeframe, startDateRange[0], startDateRange[1]);
        }
    }, [prevDisabled, onChange, startDateRange, timeframe]);

    const onEnd = useCallback(() => {
        if (!nextDisabled) {
            onChange(timeframe, endDateRange[0], endDateRange[1]);
        }
    }, [nextDisabled, onChange, endDateRange, timeframe]);

    const onPrev = useCallback(() => {
        if (!prevDisabled) {
            onChange(timeframe, nextPrevRange[0], nextPrevRange[1]);
        }
    }, [prevDisabled, onChange, nextPrevRange, timeframe]);

    const onNext = useCallback(() => {
        if (!nextDisabled) {
            const next = nextRange(timeframe, startDate, endDate);
            if (next) {
                onChange(timeframe, next[0], next[1]);
            }
        }
    }, [nextDisabled, timeframe, onChange, startDate, endDate]);

    const onDateRangeChange = useCallback(
        (start: Date, end: Date) => {
            if (
                timeframe === ReportDateTimeframe.Range ||
                timeframe === ReportDateTimeframe.Daily
            ) {
                onChange(timeframe, start, end);
            }
        },
        [onChange, timeframe],
    );

    const dateRangeLabel = useMemo(
        () => (
            <>
                {timeframe === ReportDateTimeframe.All && (
                    <Typography color="textSecondary" variant="body1">
                        <Translate path="Reporting.timeframes.timeFrameAllLabel" />
                    </Typography>
                )}
                {timeframe === ReportDateTimeframe.Daily && formatDateString({ date: startDate })}
                {[ReportDateTimeframe.Daily, ReportDateTimeframe.All].indexOf(timeframe) === -1 && (
                    <>
                        {formatDateString({ date: startDate })} -
                        {formatDateString({ date: endDate })}
                    </>
                )}
            </>
        ),
        [timeframe, formatDateString, startDate, endDate],
    );

    const showPlainTextDateLabel = useMemo(
        () => timeframe !== ReportDateTimeframe.Range && timeframe !== ReportDateTimeframe.Daily,
        [timeframe],
    );

    const showStartBtn = useMemo(
        () => timeframe !== ReportDateTimeframe.Range && showGoStart,
        [timeframe, showGoStart],
    );
    const showEndBtn = useMemo(
        () => timeframe !== ReportDateTimeframe.Range && showGoEnd,
        [timeframe, showGoEnd],
    );

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <>
                <Grid
                    container
                    spacing={2}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                    }}
                >
                    {showStartBtn && (
                        <Grid item>
                            <IconButton
                                disabled={prevDisabled}
                                onClick={onStart}
                                aria-label={t('Reporting.dateSwitcher.first')}
                            >
                                <FastRewind />
                            </IconButton>
                        </Grid>
                    )}
                    {timeframe !== ReportDateTimeframe.Range && (
                        <Grid item>
                            <IconButton
                                disabled={prevDisabled}
                                onClick={onPrev}
                                aria-label={t('Reporting.dateSwitcher.previous')}
                            >
                                <ChevronLeft />
                            </IconButton>
                        </Grid>
                    )}
                    <Grid item sx={{ pr: timeframe === ReportDateTimeframe.Range ? 2 : 0 }}>
                        {showPlainTextDateLabel && dateRangeLabel}
                        {timeframe === ReportDateTimeframe.Daily && (
                            <DateDropdown
                                dateLabel={dateRangeLabel}
                                date={startDate}
                                onChange={onDateRangeChange}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        )}
                        {timeframe === ReportDateTimeframe.Range && (
                            <DateRangeDropdown
                                dateRangeLabel={dateRangeLabel}
                                startDate={startDate}
                                endDate={endDate}
                                maxDate={maxDate}
                                minDate={minDate}
                                onChange={onDateRangeChange}
                            />
                        )}
                    </Grid>
                    {timeframe !== ReportDateTimeframe.Range && (
                        <Grid item>
                            <IconButton
                                disabled={nextDisabled}
                                onClick={onNext}
                                aria-label={t('Reporting.dateSwitcher.next')}
                            >
                                <ChevronRight />
                            </IconButton>
                        </Grid>
                    )}
                    {showEndBtn && (
                        <Grid item>
                            <IconButton
                                disabled={nextDisabled}
                                onClick={onEnd}
                                aria-label={t('Reporting.dateSwitcher.last')}
                            >
                                <FastForward />
                            </IconButton>
                        </Grid>
                    )}
                    {Array.isArray(availableTimeframes) &&
                        availableTimeframes.length > 0 &&
                        !hideDropdown && (
                            <Grid item>
                                <TimeFrameSwitcher
                                    timeFrame={timeframe}
                                    availableTimeframes={availableTimeframes}
                                    setTimeFrame={onSwitchTimeframe}
                                />
                            </Grid>
                        )}
                </Grid>
            </>
        </Box>
    );
};

export default ReportDateSwitcher;
